import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Input, Select, Switch } from 'antd';
import { useQuery } from '@common/hooks';
import { ErrorHandler, LoadingState } from '@common/components';
import { DocumentTemplates } from 'works/legacy/graphql/works-list-item-template';
import ExpirationTypeSelect from './expiration-type-select';

export default function DocumentTemplateForm({
  categories,
  handleSubmit,
  initialValues,
  showExpiration,
  form,
}) {
  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState(null);

  const { loading, error, data } = useQuery(DocumentTemplates, {
    variables: {
      categories,
    },
  });

  useEffect(() => {
    if (!data) return;
    setTypes(data.documentTemplates);

    if (!initialValues || !initialValues.documentTemplateId) return;
    const type = data.documentTemplates.find(
      (doc) => doc.id === initialValues.documentTemplateId
    );

    setSubtypes(type.types);
  }, [data, initialValues]);

  if (loading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorHandler error={error} log="FormBackgroundIdentificationTemplate" />
    );
  }

  const handleTypeSelect = (value) => {
    const type = data.documentTemplates.find((item) => item.id === value);
    setSubtypes(type.types);
    form.setFieldsValue({
      documentTemplateSubtype: null,
      name: type.name,
      expirationType: type.expirationType || 'EXPIRATION_DATE',
      daysToExpiry: type.daysToExpiry,
    });
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      initialValues={initialValues}
    >
      <Form.Item label="Active" name="active" valuePropName="checked">
        <Switch />
      </Form.Item>
      <Form.Item
        label="Document Type"
        name="documentTemplateId"
        rules={[{ required: true, message: 'Select a document type' }]}
      >
        <Select
          size="large"
          onSelect={handleTypeSelect}
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
        >
          {types.map(({ id, name }) => (
            <Select.Option key={id} value={id}>
              {name}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>

      {subtypes && (
        <Form.Item
          label="Document Sub Type"
          name="documentTemplateSubtype"
          rules={[{ required: true, message: 'Select a document sub type' }]}
        >
          <Select size="large">
            {subtypes.map((value) => {
              return (
                <Select.Option key={value} value={value}>
                  {value}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>
      )}

      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: 'Please enter a item name' }]}
      >
        <Input size="large" />
      </Form.Item>

      {showExpiration && <ExpirationTypeSelect form={form} />}

      <Form.Item label="Description" name="description">
        <Input.TextArea />
      </Form.Item>
    </Form>
  );
}

DocumentTemplateForm.propTypes = {
  initialValues: PropTypes.shape({
    active: PropTypes.bool,
    documentTemplateId: PropTypes.number,
    documentTemplateSubtype: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
  }),
  dismiss: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(PropTypes.string).isRequired,
  form: PropTypes.object.isRequired,
  showExpiration: PropTypes.bool,
};

DocumentTemplateForm.defaultProps = {
  showExpiration: false,
};
